import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, merge, asyncScheduler } from 'rxjs';
import { throttleTime, debounceTime } from 'rxjs/operators';
import { RootEntityContextService } from '../shared/root-entity-context.service';
import { TabOptions } from '../shared/enums/remediation-tab-options.enum';
import { CUSTOMAPIService } from 'app/custom-api.service';

const DEFAULT_PAGE_SIZE = 18;
const RELOAD_DEBOUNCE_MS = 4000;
const RELOAD_MAX_WAIT_MS = 15000;

export interface RemediationContext {
  rootEntityId: string;
  assessmentId?: string;
  riskContext?: { assessmentId?: string; controlIds?: string[]; smartMappingIds?: string[] };
  itcScoreFilter?: boolean;
}

export interface TabCounts {
  MY_TASKS: number;
  READINESS: number;
  COMPLIANCE: number;
  INTERNAL_TASKS: number;
  ALL_TASKS?: number;
}

const EMPTY_TAB_COUNTS: TabCounts = { MY_TASKS: 0, READINESS: 0, COMPLIANCE: 0, INTERNAL_TASKS: 0, ALL_TASKS: 0 };

@Injectable({ providedIn: 'root' })
export class RemediationDataService implements OnDestroy {
  private itemsSubject = new BehaviorSubject<any[]>([]);
  private tabCountsSubject = new BehaviorSubject<TabCounts>({ ...EMPTY_TAB_COUNTS });
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingMoreSubject = new BehaviorSubject<boolean>(false);
  private selectionSubject = new BehaviorSubject<{ selectAll: boolean; count: number }>({ selectAll: false, count: 0 });

  readonly items$: Observable<any[]> = this.itemsSubject.asObservable();
  readonly tabCounts$: Observable<TabCounts> = this.tabCountsSubject.asObservable();
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  readonly loadingMore$: Observable<boolean> = this.loadingMoreSubject.asObservable();
  readonly selection$ = this.selectionSubject.asObservable();
  readonly bulkRemediateStarted$ = new Subject<string[]>();
  readonly bulkRemediateCompleted$ = new Subject<{ answered: any[]; failedIds: string[] }>();

  private loadingMore = false;

  private context: RemediationContext = { rootEntityId: null };
  private currentTab: TabOptions = TabOptions.READINESS;
  private filters: Record<string, any> = {};
  private sort: string = null;
  // Per-tab next-page cursor. Reset when filters/sort change (the backend also invalidates a
  // cursor across filter/sort changes via its fingerprint, but resetting locally avoids a wasted
  // round-trip).
  private cursors: Record<string, string> = {};

  // Selection recipe (PRD §4.3). Two modes: explicit include (selectedIds), or select-all-over-
  // the-filtered-set (selectAll=true) with per-row exclusions.
  private selectAll = false;
  private selectedIds = new Set<string>();
  private uncheckedAfterSelectAll = new Set<string>();
  private filtersSnapshot: Record<string, any> = {};

  private subscriptions: Subscription[] = [];

  constructor(
    private customApiService: CUSTOMAPIService,
    private rootEntityContextService: RootEntityContextService
  ) {
    // Reload the current view when the root's entities/assessments change under us.
    const updates$ = merge(
      this.rootEntityContextService.onEntityUpdated,
      this.rootEntityContextService.onAssessmentUpdated
    );
    this.subscriptions.push(
      merge(
        updates$.pipe(debounceTime(RELOAD_DEBOUNCE_MS, asyncScheduler)),
        updates$.pipe(throttleTime(RELOAD_MAX_WAIT_MS, asyncScheduler, { leading: false, trailing: true }))
      )
        // Collapse the two triggers if they land together; reloadIfReady's own in-flight guard covers the rest.
        .pipe(throttleTime(500, asyncScheduler, { leading: true, trailing: false }))
        .subscribe(() => void this.reloadIfReady())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  // --- configuration -------------------------------------------------------

  setContext(context: RemediationContext): void {
    this.context = context || { rootEntityId: null };
    this.resetPaging();
  }

  getTab(): TabOptions {
    return this.currentTab;
  }

  setTab(tab: TabOptions): Promise<void> {
    this.currentTab = tab;
    this.clearSelection();
    return this.loadPage();
  }

  setFilters(filters: Record<string, any>): Promise<void> {
    this.filters = this.mapUiFiltersToServer(filters);
    this.cursors = {}; // filters changed — every tab's cursor is stale
    this.clearSelection();
    return this.loadPage();
  }

  // Translate the component's UI filter shape (`selected*List`) into the server contract
  // (`status`/`severity`/`frameworks`/`entities`/`searchText`).
  mapUiFiltersToServer(ui: Record<string, any>): Record<string, any> {
    if (!ui) {
      return {};
    }
    // Already server-shaped (e.g. programmatic callers) — pass through.
    if (
      !ui.selectedStatusList &&
      !ui.selectedSeverityList &&
      !ui.selectedFrameworkList &&
      !ui.selectedEntityList &&
      !ui.selectedDynamicLabels
    ) {
      return { ...ui };
    }
    const out: Record<string, any> = {};
    const status = (ui.selectedStatusList || []).map((s: string) => (s || '').toUpperCase());
    if (status.length) {
      out.status = status;
    }
    const severity = (ui.selectedSeverityList || []).map((s: string) => (s || '').toLowerCase());
    if (severity.length) {
      out.severity = severity;
    }
    // Strip the UI-only 'all' token; only real framework keys narrow (empty = every framework).
    const frameworks = (ui.selectedFrameworkList || []).filter((k: string) => k && k.toLowerCase() !== 'all');
    if (frameworks.length) {
      out.frameworks = frameworks;
    }
    // Maturity is a multi-select 0–5 list; strip the 'All' token (empty = every maturity).
    const maturity = (ui.selectedMaturityList || []).filter(
      (m: any) => m !== null && m !== undefined && `${m}`.toLowerCase() !== 'all'
    );
    if (maturity.length) {
      out.maturity = maturity;
    }
    const entities = (ui.selectedEntityList || []).filter((e: string) => e && e !== 'All');
    if (entities.length) {
      out.entities = entities;
    }
    // Assigned-users filter: send the selected user ids (strip the 'all' token). The backend resolves
    // the assigned task set and narrows list + counts.
    const users = (ui.selectedUsersList || []).filter((id: string) => id && `${id}`.toLowerCase() !== 'all');
    if (users.length) {
      out.users = users;
    }
    const searchText = ui.searchText || ui.searchString;
    if (searchText) {
      out.searchText = searchText;
    }
    // Dynamic Labels: catalog ids the backend matches against per-target parentTagId/categoryId.
    const labels = (ui.selectedDynamicLabels || []).filter((id: string) => !!id);
    if (labels.length) {
      out.labels = labels;
    }
    return out;
  }

  setSort(sort: string): Promise<void> {
    this.sort = sort;
    this.cursors = {};
    return this.loadPage();
  }

  // Apply sort + filters together in ONE round-trip (the security-control-tools bar emits both a
  // search query and a Critical/Low sort in a single event; calling setSort + setFilters would
  // fire two loads).
  setSortAndFilters(sort: string, filters: Record<string, any>): Promise<void> {
    this.sort = sort;
    this.filters = this.mapUiFiltersToServer(filters);
    this.cursors = {};
    this.clearSelection();
    return this.loadPage();
  }

  // Initial landing load: set the active tab + filters (and any context already set) and fetch page
  // 1 in a single round-trip. Used by the component's initServerRemediation so context/filters/tab
  // don't each trigger their own fetch.
  loadInitial(tab: TabOptions, filters: Record<string, any>): Promise<void> {
    this.currentTab = tab;
    this.filters = this.mapUiFiltersToServer(filters);
    this.cursors = {};
    this.clearSelection();
    return this.loadPage();
  }

  // --- query ---------------------------------------------------------------

  // Normalize a server item into the exact shape cygov-security-control-card reads. This is
  // NORMALIZATION ONLY — no cross-collection lookups, no enrichment, no filtering.
  private mapServerItemToCard(item: any): any {
    if (!item) {
      return item;
    }
    const labels = Array.isArray(item.labels) ? item.labels : [];
    return {
      ...item,
      cardId: item.cardId || item.id,
      usersAssignments: { items: this.uniqueAssignments((item.usersAssignments && item.usersAssignments.items) || []) },
      answers: item.answers || [],
      labels,
      labelGroups: this.buildLabelGroups(labels),
    };
  }

  // Group labels by `type` + source (Tags/Categories) for the table's "See More" popup.
  private buildLabelGroups(labels: any[]): { type: string; source: string; items: any[] }[] {
    const groupMap = new Map<string, { type: string; source: string; items: any[] }>();
    (labels || []).forEach(label => {
      const key = `${label.type}_${label.source}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, { type: label.type, source: label.source, items: [] });
      }
      groupMap.get(key).items.push(label);
    });
    return Array.from(groupMap.values());
  }

  // Dedupe assignment rows by user id; drop rows without a user (deleted users).
  private uniqueAssignments(items: any[]): any[] {
    const seen = new Set<string>();
    return (items || []).filter(assignment => {
      const id = assignment?.user?.id;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }

  private buildQueryBody(extra: Record<string, any> = {}): { rootEntityId: string; [key: string]: any } {
    return {
      rootEntityId: this.context.rootEntityId,
      assessmentId: this.context.assessmentId,
      riskContext: this.context.riskContext,
      itcScoreFilter: this.context.itcScoreFilter,
      tab: this.currentTab,
      filters: this.filters,
      sort: this.sort,
      limit: DEFAULT_PAGE_SIZE,
      ...extra,
    };
  }

  // When gated (e.g. the third-party view with no vendor picked yet) NO query is issued — this also
  // blocks RECS-driven reloads so a root-wide first-party fetch can never leak into a third-party view.
  private gated = false;

  // Third-party is always scoped to one vendor; with none selected we show nothing. Gate suppresses all
  // fetches and clears the current page/counts.
  setGated(gated: boolean): void {
    this.gated = gated;
    if (gated) {
      this.clear();
    }
  }

  clear(): void {
    this.itemsSubject.next([]);
    this.tabCountsSubject.next({ ...EMPTY_TAB_COUNTS });
    this.resetPaging();
  }

  // `silent` refreshes the data WITHOUT toggling the blocking loader — used for background
  async loadPage(opts: { silent?: boolean } = {}): Promise<void> {
    // Any real load (explicit user action, or a non-suppressed RECS reload) reconciles to authoritative
    // state, so clear the post-save suppression. reloadIfReady returns BEFORE reaching here while
    // suppressed, so a suppressed auto-reload never clears its own flag.
    this.reloadSuppressed = false;
    if (!this.context.rootEntityId || this.gated) {
      return;
    }
    if (!opts.silent) {
      this.loadingSubject.next(true);
    }
    try {
      const res = await this.customApiService.fetchRemediationTasks(this.buildQueryBody());
      this.itemsSubject.next(((res && res.items) || []).map(i => this.mapServerItemToCard(i)));
      this.cursors[this.currentTab] = (res && res.cursor) || null;
      this.tabCountsSubject.next({ ...EMPTY_TAB_COUNTS, ...((res && res.tabCounts) || {}) });
    } finally {
      if (!opts.silent) {
        this.loadingSubject.next(false);
      }
    }
  }

  // Refresh ONLY the tab-count badges from the server tallies (countMapper), without touching the
  // rendered page, cursors, or scroll position. Used after a task action so the badges reflect the
  // authoritative totals (a `limit: 1` fetch — tabCounts are computed independently of page size).
  async refreshCounts(): Promise<void> {
    // the task action also moved the vendor Questionnaire tab's cached open-gap counts
    this.rootEntityContextService.clearVendorOpenTaskCounts();
    if (!this.context.rootEntityId) {
      return;
    }
    const res = await this.customApiService.fetchRemediationTasks(this.buildQueryBody({ limit: 1 }));
    if (res && res.tabCounts) {
      this.tabCountsSubject.next({ ...EMPTY_TAB_COUNTS, ...res.tabCounts });
    }
  }

  async loadMore(): Promise<void> {
    if (this.loadingMore || !this.context.rootEntityId) {
      return;
    }
    const tab = this.currentTab;
    const cursor = this.cursors[tab];
    if (!cursor) {
      return; // no more pages
    }
    this.loadingMore = true;
    this.loadingMoreSubject.next(true);
    try {
      const res = await this.customApiService.fetchRemediationTasks(this.buildQueryBody({ cursor }));
      if (tab !== this.currentTab) {
        return; // tab switched mid-fetch — drop this stale page
      }
      const mapped = ((res && res.items) || []).map(i => this.mapServerItemToCard(i));
      this.itemsSubject.next([...this.itemsSubject.value, ...mapped]);
      this.cursors[this.currentTab] = (res && res.cursor) || null;
      if (res && res.tabCounts) {
        this.tabCountsSubject.next({ ...EMPTY_TAB_COUNTS, ...res.tabCounts });
      }
    } finally {
      this.loadingMore = false;
      this.loadingMoreSubject.next(false);
    }
  }

  hasMore(): boolean {
    return !!this.cursors[this.currentTab];
  }

  // Full filtered set in one response (export/download) — does not touch the paged list state.
  async exportAll(tab?: TabOptions): Promise<any[]> {
    if (!this.context.rootEntityId) {
      return [];
    }
    const extra: Record<string, any> = { exportMode: true };
    if (tab) {
      extra.tab = tab;
    }
    const res = await this.customApiService.fetchRemediationTasks(this.buildQueryBody(extra));
    return (res && res.items) || [];
  }

  // --- selection -----------------------------------------------------------

  isSelected(taskId: string): boolean {
    return this.selectAll ? !this.uncheckedAfterSelectAll.has(taskId) : this.selectedIds.has(taskId);
  }

  toggle(taskId: string): void {
    if (this.selectAll) {
      if (this.uncheckedAfterSelectAll.has(taskId)) {
        this.uncheckedAfterSelectAll.delete(taskId);
      } else {
        this.uncheckedAfterSelectAll.add(taskId);
      }
    } else if (this.selectedIds.has(taskId)) {
      this.selectedIds.delete(taskId);
    } else {
      this.selectedIds.add(taskId);
    }
    this.emitSelection();
  }

  // "Select all tasks matching the current filter" — snapshots the filters so the server resolves
  // the same set later even if the user keeps scrolling/changing things before applying.
  selectAllMatching(): void {
    this.selectAll = true;
    this.selectedIds.clear();
    this.uncheckedAfterSelectAll.clear();
    this.filtersSnapshot = { ...this.filters };
    this.emitSelection();
  }

  clearSelection(): void {
    this.selectAll = false;
    this.selectedIds.clear();
    this.uncheckedAfterSelectAll.clear();
    this.filtersSnapshot = {};
    this.emitSelection();
  }

  // The recipe the label-action route resolves server-side (PRD §4.3).
  buildSelectionRecipe(): {
    mode: 'include' | 'exclude';
    taskIds: string[];
    filters?: Record<string, any>;
    tab?: string;
    assessmentId?: string;
    riskContext?: any;
  } {
    if (this.selectAll) {
      return {
        mode: 'exclude',
        taskIds: [...this.uncheckedAfterSelectAll],
        filters: this.filtersSnapshot,
        tab: this.currentTab,
        assessmentId: this.context.assessmentId,
        riskContext: this.context.riskContext,
      };
    }
    return { mode: 'include', taskIds: [...this.selectedIds] };
  }

  private emitSelection(): void {
    const count = this.selectAll
      ? Math.max(0, (this.tabCountsSubject.value[this.currentTab] || 0) - this.uncheckedAfterSelectAll.size)
      : this.selectedIds.size;
    this.selectionSubject.next({ selectAll: this.selectAll, count });
  }

  // --- label actions -------------------------------------------------------

  async assignLabel(labelPayload: Record<string, any>): Promise<any> {
    return this.runLabelAction('assignLabel', labelPayload);
  }

  async removeLabel(labelPayload: Record<string, any>): Promise<any> {
    return this.runLabelAction('removeLabel', labelPayload);
  }

  async assignLabelForTask(taskId: string, labelPayload: Record<string, any>): Promise<any> {
    return this.labelActionForTask('assignLabel', taskId, labelPayload);
  }

  async removeLabelForTask(taskId: string, labelPayload: Record<string, any>, reload = true): Promise<any> {
    const result = await this.labelActionForTask('removeLabel', taskId, labelPayload);
    if (reload) {
      await this.loadPage();
    }
    return result;
  }

  private async labelActionForTask(
    action: 'assignLabel' | 'removeLabel',
    taskId: string,
    labelPayload: Record<string, any>
  ): Promise<any> {
    return this.customApiService.remediationLabelAction({
      rootEntityId: this.context.rootEntityId,
      action,
      labelPayload,
      selection: { mode: 'include', taskIds: [taskId] },
    });
  }

  async assignLabelToAllMatching(labelPayload: Record<string, any>): Promise<any> {
    return this.customApiService.remediationLabelAction({
      rootEntityId: this.context.rootEntityId,
      action: 'assignLabel',
      labelPayload,
      selection: {
        mode: 'exclude',
        taskIds: [],
        filters: this.filters,
        tab: this.currentTab,
        assessmentId: this.context.assessmentId,
        riskContext: this.context.riskContext,
      },
    });
  }

  // Force a re-fetch of the current page (used after a batch of label writes).
  async reloadCurrentPage(): Promise<void> {
    await this.loadPage();
  }

  private async runLabelAction(action: 'assignLabel' | 'removeLabel', labelPayload: Record<string, any>): Promise<any> {
    const result = await this.customApiService.remediationLabelAction({
      rootEntityId: this.context.rootEntityId,
      action,
      labelPayload,
      selection: this.buildSelectionRecipe(),
    });
    this.clearSelection();
    await this.loadPage(); // reflect new/removed label chips
    return result;
  }

  // --- lifecycle -----------------------------------------------------------

  private resetPaging(): void {
    this.cursors = {};
    this.itemsSubject.next([]);
    this.tabCountsSubject.next({ ...EMPTY_TAB_COUNTS });
    this.clearSelection();
  }

  reset(): void {
    this.context = { rootEntityId: null };
    this.currentTab = TabOptions.READINESS;
    this.filters = {};
    this.sort = null;
    this.resetPaging();
  }

  // While the Remediate modal pages the shared list, a RECS-driven reload (loadPage) would REPLACE
  // items$ with page 1 and wipe the modal mid-remediation. Pause those background reloads until the
  // modal closes. User-driven loads (loadMore/setFilters/setTab/…) are unaffected.
  private pagingPaused = false;
  setPagingPaused(paused: boolean): void {
    this.pagingPaused = paused;
  }

  // After a remediate action the view is patched in place and must stay stable — the RECS auto-reload
  // (score change → reloadIfReady → loadPage) would refetch page 1 and drop the just-updated rows.
  // Suppress ONLY that auto-reload; any explicit user load (loadPage) clears it, so filter/tab/sort/
  // refresh still reconcile to authoritative server state.
  private reloadSuppressed = false;
  setReloadSuppressed(suppressed: boolean): void {
    this.reloadSuppressed = suppressed;
  }

  private reloading = false;
  private async reloadIfReady(): Promise<void> {
    if (!this.context.rootEntityId || this.pagingPaused || this.reloadSuppressed || this.reloading) {
      return;
    }
    this.reloading = true;
    try {
      await this.loadPage({ silent: true });
    } finally {
      this.reloading = false;
    }
  }
}
